// Phase APP-4 · Packer Pending Splash — /packer/pending
// Mobile-first. Renders 3 states based on /api/auth/me approval_status:
//   pending           → orange awaiting banner + MHM info
//   rejected_1        → red rejection banner + reason + Lead contact
//   rejected_terminal → dark-red declined banner + admin contact
// Auto-polls /api/auth/me every 30s. Flips to /packer/dashboard once active.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut, RefreshCw, Mail, Phone, ShieldAlert,
          CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../../services/api";
import useAuthStore from "../../store/authStore";

const ORANGE = "#FF6B00";
const BUILD_STAMP = "APP-1.0-r7";
const ADMIN_CONTACT = "admin@pickngo.com";

export default function PackerPendingSplash() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [me, setMe] = useState(user);
  const [busy, setBusy] = useState(false);
  const [reasonDoc, setReasonDoc] = useState(null);

  const status = (me?.approval_status || "pending").toLowerCase();

  // If the user is already active, redirect immediately.
  useEffect(() => {
    if (status === "active") {
      navigate("/packer/dashboard", { replace: true });
    }
  }, [status, navigate]);

  // Pull the latest /me + the rejection details (last packer_request for me).
  const refresh = async () => {
    setBusy(true);
    try {
      const r = await api.get("/auth/me");
      setMe(r.data);
      if (typeof setUser === "function") setUser(r.data);
      if (r.data?.approval_status === "active") {
        toast.success("You're approved! Redirecting...");
        setTimeout(() => navigate("/packer/dashboard", { replace: true }), 800);
        return;
      }
      // Pull the related approval doc (if any) for the rejection reason.
      try {
        const rel = await api.get("/packer/my-approval");
        setReasonDoc(rel.data);
      } catch (_) { /* endpoint optional */ }
    } catch (_) { /* keep stale me */ }
    finally { setBusy(false); }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scope = me?.scope || {};
  const microHubId = scope.micro_hub_id || me?.hub_id;
  const mainHubName = scope.main_hub_name || scope.main_hub_id || "Main Hub";
  const leadName = reasonDoc?.lead?.name || scope.lead_name || "your Lead";
  const leadPhone = reasonDoc?.lead?.phone || scope.lead_phone || "";
  const leadEmail = reasonDoc?.lead?.email || scope.lead_email || "";
  const lastReject = (reasonDoc?.approval_history || [])
                        .slice().reverse()
                        .find((h) => h.action === "rejected");

  return (
    <div className="min-h-[100dvh] bg-slate-50 flex flex-col"
         data-testid="packer-pending-splash"
         data-approval-status={status}>
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-2">
        <ShieldAlert className="h-5 w-5" style={{ color: ORANGE }}/>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Packer Portal</div>
          <div className="text-sm font-semibold text-slate-900"
               data-testid="packer-pending-name">{me?.name || "—"}</div>
        </div>
        <button onClick={refresh} disabled={busy}
                aria-label="Refresh"
                data-testid="packer-pending-refresh"
                className="p-1.5 rounded-full hover:bg-slate-100">
          {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : <RefreshCw className="h-4 w-4"/>}
        </button>
      </header>

      <main className="flex-1 max-w-md w-full mx-auto px-4 py-6 space-y-4">
        {/* Banner block (status-driven) */}
        {status === "pending" && (
          <div className="rounded-2xl px-4 py-5 text-white"
               data-testid="packer-pending-banner-pending"
               style={{ backgroundColor: ORANGE }}>
            <div className="text-xs uppercase tracking-wider opacity-80">Status</div>
            <h1 className="text-2xl font-bold mt-1">Awaiting Approval</h1>
            <p className="text-sm mt-2 opacity-95">
              Your account is pending approval from {mainHubName}.
              You&apos;ll be able to start work once the Main Hub Manager
              approves your request.
            </p>
          </div>
        )}
        {status === "rejected_1" && (
          <div className="bg-rose-600 rounded-2xl px-4 py-5 text-white"
               data-testid="packer-pending-banner-rejected1">
            <div className="text-xs uppercase tracking-wider opacity-80">Status</div>
            <h1 className="text-2xl font-bold mt-1">Request Rejected</h1>
            {lastReject ? (
              <div className="bg-white/15 rounded-lg p-3 mt-3 text-sm">
                <div className="text-xs uppercase tracking-wider opacity-80">Reason</div>
                {lastReject.category && (
                  <div className="font-mono text-xs mt-1">
                    {lastReject.category}
                  </div>
                )}
                {lastReject.remarks && (
                  <div className="mt-1 whitespace-pre-wrap break-words">
                    {lastReject.remarks}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm mt-2 opacity-95">
                Your request needs revision before it can be approved.
              </p>
            )}
            <p className="text-sm mt-3 opacity-95">
              Please contact {leadName} to update your request and resubmit.
            </p>
          </div>
        )}
        {status === "rejected_terminal" && (
          <div className="bg-rose-900 rounded-2xl px-4 py-5 text-white"
               data-testid="packer-pending-banner-terminal">
            <div className="text-xs uppercase tracking-wider opacity-80">Status</div>
            <h1 className="text-2xl font-bold mt-1">Account Not Approved</h1>
            <p className="text-sm mt-2 opacity-95">
              Your account has been declined. Please contact admin at{" "}
              <a href={`mailto:${ADMIN_CONTACT}`}
                 className="underline decoration-white/50"
                 data-testid="packer-pending-admin-mail">{ADMIN_CONTACT}</a>{" "}
              for further information.
            </p>
          </div>
        )}
        {status === "active" && (
          <div className="bg-emerald-600 rounded-2xl px-4 py-5 text-white"
               data-testid="packer-pending-banner-active">
            <CheckCircle2 className="h-6 w-6 mb-1"/>
            <h1 className="text-2xl font-bold">You&apos;re approved!</h1>
            <p className="text-sm mt-2 opacity-95">Redirecting to your dashboard…</p>
          </div>
        )}

        {/* Support contact block (always visible if we know a Lead) */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-2"
             data-testid="packer-pending-contact-block">
          <div className="text-xs uppercase tracking-wider text-slate-500 inline-flex items-center gap-1">
            <AlertTriangle className="h-3 w-3"/> Need help? Contact your Lead
          </div>
          <div className="text-sm font-semibold text-slate-900"
               data-testid="packer-pending-lead-name">{leadName}</div>
          {leadPhone && (
            <a href={`tel:${leadPhone}`}
               className="block text-sm text-slate-700 inline-flex items-center gap-1.5"
               data-testid="packer-pending-lead-phone">
              <Phone className="h-3.5 w-3.5"/> {leadPhone}
            </a>
          )}
          {leadEmail && (
            <a href={`mailto:${leadEmail}`}
               className="block text-sm text-slate-700 inline-flex items-center gap-1.5"
               data-testid="packer-pending-lead-email">
              <Mail className="h-3.5 w-3.5"/> {leadEmail}
            </a>
          )}
          {microHubId && (
            <div className="text-[10px] font-mono text-slate-400">
              Hub: {microHubId}
            </div>
          )}
        </div>

        <button onClick={() => { logout?.(); navigate("/login"); }}
                data-testid="packer-pending-logout"
                className="w-full text-sm text-slate-600 hover:text-rose-600 inline-flex items-center justify-center gap-1.5 py-2">
          <LogOut className="h-3.5 w-3.5"/> Logout
        </button>
      </main>

      <footer className="text-center py-2 text-[10px] font-mono text-slate-400"
              data-testid="packer-pending-build-stamp">
        {BUILD_STAMP}
      </footer>
    </div>
  );
}
